import { useState } from "react";
import { useTranslation } from "react-i18next";
import CommonStyles from "@components/CommonStyles";
import ServiceAddressFields, { IServiceAddress } from "@components/PackageBooking/ServiceAddressFields";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import profileService from "@base/modules/user/profile/profileService";

interface IProps {
  open: boolean;
  toggle: () => void;
  record: IPatientRecord;
  onSaved: (record: IPatientRecord) => void;
}

/**
 * An insurance booking needs the patient's full address on the record (§9.2 step 2). Saved with
 * every other field of the record kept as it was.
 */
export default function AddressUpdateModal({ open, toggle, record, onSaved }: IProps) {
  //! state
  const { t } = useTranslation();
  const [address, setAddress] = useState<IServiceAddress>({
    provinceCode: record.administrative_province_code,
    provinceName: record.administrative_province_name,
    wardCode: record.administrative_ward_code,
    wardName: record.administrative_ward_name,
    street: record.patient_address,
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  //! function
  const isComplete = !!address.provinceCode && !!address.wardCode && !!address.street?.trim();

  const save = async () => {
    const updated: IPatientRecord = {
      ...record,
      patient_address: address.street?.trim(),
      administrative_province_code: address.provinceCode,
      administrative_province_name: address.provinceName,
      administrative_ward_code: address.wardCode,
      administrative_ward_name: address.wardName,
      administrative_district_code: "",
      administrative_district_name: "",
    };

    try {
      setIsSaving(true);
      setError("");
      const resp = await profileService.updatePatientRecord(updated, String(record.id));

      onSaved({ ...updated, ...(resp.data ?? {}) });
    } catch (saveError) {
      setError(t("booking.addressUpdateFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  //! render
  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      title={record.patient_name}
      okText={t("booking.saveAddress")}
      okButtonProps={{ loading: isSaving, disabled: !isComplete }}
      onConfirm={save}
      content={
        <div className="flex flex-col gap-3">
          <ServiceAddressFields label="" labeledFields required value={address} onChange={setAddress} />
          {error ? <span className="text-common-red500 text-sm font-normal font-['Inter']">{error}</span> : null}
        </div>
      }
    />
  );
}
